// Hermes run-cap warning helper.
//
// Pure JS so node:test can import it without a JSX transpiler. ESM
// to match the rest of pwa/ (the Vite bundler + node:test treat
// .js as ESM under pwa/package.json's "type": "module").
//
// Computes whether an active Hermes run is approaching one of its
// three caps (steps / cost / tools). Returns the worst-dimension
// warning level so the UI can render a single chip rather than
// three separate ones. The runner itself terminates at 100% --
// this helper is a soft heads-up at 80% / 95% so the user can
// choose to pause + adjust before the run dies.
//
// Levels:
//   'ok'     -- below 80% on all dimensions (or run isn't active)
//   'warn'   -- 80-94% on at least one dimension (yellow chip)
//   'danger' -- 95%+ on at least one dimension (red chip)

export const WARN_THRESHOLD   = 0.80;
export const DANGER_THRESHOLD = 0.95;

export const TERMINAL_STATUSES = new Set([
  "done", "failed", "cancelled", "expired",
  "capped_steps", "capped_dollars", "capped_tools",
  "failed_length_check",
]);

function _pct(used, cap) {
  const u = Number(used) || 0;
  const c = Number(cap) || 0;
  if (c <= 0) return 0;
  return u / c;
}

// Given a run row from /api/hermes/runs/:id, returns:
//   { level, dimension, pct, dimensions: { steps, cost, tools } }
//
// `dimension` is the worst single dimension that drove the level
// (so the chip can say "cost 92%"). When level === 'ok', dimension
// is null and the UI shouldn't render a chip.
export function computeCapWarning(run) {
  if (!run || typeof run !== "object") {
    return { level: "ok", dimension: null, pct: 0, dimensions: {} };
  }
  // Terminal runs don't get warnings (the run is already over).
  if (run.status && TERMINAL_STATUSES.has(run.status)) {
    return { level: "ok", dimension: null, pct: 0, dimensions: {} };
  }
  const dimensions = {
    steps: _pct(run.step_count, run.step_cap),
    cost:  _pct(run.cost_cents_used, run.dollar_cap_cents),
    tools: _pct(run.tool_call_count, run.tool_call_cap),
  };
  let worst = { dim: null, pct: 0 };
  for (const [dim, pct] of Object.entries(dimensions)) {
    if (pct > worst.pct) worst = { dim, pct };
  }
  let level = "ok";
  if (worst.pct >= DANGER_THRESHOLD) level = "danger";
  else if (worst.pct >= WARN_THRESHOLD) level = "warn";
  return {
    level,
    dimension: level === "ok" ? null : worst.dim,
    pct: worst.pct,
    dimensions,
  };
}

// Render-helper: formats the chip label.
export function formatCapWarningLabel(warning) {
  if (!warning || warning.level === "ok") return "";
  const pct = Math.floor((warning.pct || 0) * 100);
  const dim = warning.dimension;
  if (!dim) return pct + "%";
  return dim + " " + pct + "%";
}

// H23: burn-rate projection. Looks at the most recent N assistant
// steps and computes the average cost-cents per turn; projects how
// many more turns the run can afford given remaining dollar-cap
// budget. Pure -- no side effects, safe to call every render.
//
// Returns:
//   { etaTurns,            // integer >= 0 or null when not projectable
//     avgCostCentsPerTurn, // float, >= 0
//     remainingCents,      // int, dollar_cap_cents - cost_cents_used
//     window               // how many recent turns were sampled
//   }
//
// Returns null entirely when:
//   - The run is terminal (no projection meaningful)
//   - There are zero recent assistant steps (haven't started yet)
//   - The recent-cost window sums to zero (e.g. zero-token turns;
//     can't extrapolate from a divide-by-zero)
//
// Window default: 3 most recent assistant steps. That's enough to
// smooth a single anomalous turn (e.g. one giant context blow-up)
// without leaning so far into history that an early-turn outlier
// dominates a late-run projection.
export function computeBurnRateProjection(run, steps, windowSize) {
  if (!run || typeof run !== "object") return null;
  if (run.status && TERMINAL_STATUSES.has(run.status)) return null;
  const N = Math.max(1, Math.min(20, windowSize || 3));
  const usedCents = Number(run.cost_cents_used) || 0;
  const capCents = Number(run.dollar_cap_cents) || 0;
  const remainingCents = Math.max(0, capCents - usedCents);
  if (capCents <= 0) return null;
  if (!Array.isArray(steps) || steps.length === 0) return null;
  // Pull the last N assistant-role steps with a numeric cost_cents.
  const recent = [];
  for (let i = steps.length - 1; i >= 0 && recent.length < N; i--) {
    const s = steps[i];
    if (!s || s.role !== "assistant") continue;
    // null / undefined cost_cents (legacy rows) must be skipped --
    // Number(null) is 0 (finite), which would silently inflate the
    // window count. Filter the nullish values BEFORE coercion.
    if (s.cost_cents == null) continue;
    const c = Number(s.cost_cents);
    if (!Number.isFinite(c)) continue;
    recent.push(c);
  }
  if (recent.length === 0) return null;
  const sum = recent.reduce((a, b) => a + b, 0);
  if (sum <= 0) return null;
  const avgCostCentsPerTurn = sum / recent.length;
  const etaTurns = avgCostCentsPerTurn > 0
    ? Math.floor(remainingCents / avgCostCentsPerTurn)
    : null;
  return { etaTurns, avgCostCentsPerTurn, remainingCents, window: recent.length };
}

// Render-helper: short string for the chip tooltip ("~3 turns to cap").
// Returns "" when the projection isn't useful (null, zero ETA, infinity).
export function formatBurnRateETA(projection) {
  if (!projection || projection.etaTurns === null || projection.etaTurns === undefined) return "";
  const eta = projection.etaTurns;
  if (!Number.isFinite(eta)) return "";
  if (eta <= 0) return "next turn likely caps";
  if (eta === 1) return "~1 turn to cap";
  return "~" + eta + " turns to cap";
}

// H25: forgotten-paused-run nudge.
//
// Returns the number of minutes a run has been in 'paused' state, or
// null if the run isn't paused / has no parseable updated_at. The
// runs-list row uses this to surface a "paused 1h12m" chip when a
// paused run is older than PAUSED_NUDGE_THRESHOLD_MIN -- catches the
// case where an operator paused a run and then forgot to /resume it.
//
// Pure: takes an optional nowMs for deterministic testing.
export function computePausedAge(run, nowMs) {
  if (!run || typeof run !== "object") return null;
  if (run.status !== "paused") return null;
  const t = run.updated_at || run.created_at;
  if (!t) return null;
  const updated = Date.parse(t);
  if (!Number.isFinite(updated)) return null;
  const now = Number.isFinite(nowMs) ? nowMs : Date.now();
  const diffMs = now - updated;
  if (diffMs < 0) return 0;
  return Math.floor(diffMs / 60000);
}

// Threshold below which the row stays quiet. 30 minutes is the sweet
// spot from informal admin feedback -- shorter than that is noisy
// (admins are typically actively triaging), longer hides genuinely
// forgotten runs.
export const PAUSED_NUDGE_THRESHOLD_MIN = 30;

// Humanize the minute count for the chip. Format choices:
//   m < 60       -> "paused 42m"
//   m < 1 day    -> "paused 1h12m" (omit minutes when exactly on the hour)
//   else         -> "paused 1d2h"  (omit hours when exactly on the day)
//
// Negative / non-finite -> "" (caller renders nothing).
export function formatPausedAge(minutes) {
  if (!Number.isFinite(minutes) || minutes < 0) return "";
  const m = Math.floor(minutes);
  if (m < 60) return "paused " + m + "m";
  if (m < 1440) {
    const h = Math.floor(m / 60);
    const r = m % 60;
    return r > 0 ? "paused " + h + "h" + r + "m" : "paused " + h + "h";
  }
  const d = Math.floor(m / 1440);
  const remM = m - d * 1440;
  const h = Math.floor(remM / 60);
  return h > 0 ? "paused " + d + "d" + h + "h" : "paused " + d + "d";
}
